/**
 * HeatSourceLabels — LUSTRO słownika prezentacyjnego źródeł ciepła.
 *
 * KANON: `mobile/src/constants/heatSourceLabels.ts`. Parzystość pilnuje
 * `backend/test/heatSourceLabelsParity.test.js` (wzorzec luster jak `StatusMap.ts`
 * i `STATUS_LABELS`) — zmieniasz jedno, zmieniasz drugie, inaczej bramka czerwona.
 *
 * Decyzja właściciela 2026-07-29: SKRÓTY WSZĘDZIE. Web-admin miał dotąd starą kolejność CEEB
 * i pełne etykiety, mobilka skróty — ta sama rzecz nazywana dwoma językami.
 *
 * ⛔ Warstwa prezentacji. Klucze (`value`) to kontrakt z CEEB i pozostają nietknięte — do CEEB
 * idą klucze przez `backend/src/scripts/heatSourceMappers/translate.js`, który ma własne
 * literały opcji CEEB i nie czyta tego pliku ani jego kanonu.
 *
 * ⚠️ `resolveHeatCharacter` NORMALIZUJE (pomiar 2026-07-29 na dev DB): kolumna
 * `heat_sources.heat_character` niesie enumy mobilki (7 wierszy) ORAZ surowe etykiety CEEB
 * z importu lustra (9025 wierszy). Web-admin renderuje głównie te drugie — bez normalizacji
 * skróty nie pojawiłyby się dokładnie tam, gdzie są prawdziwe dane.
 */

/** Skróty typów źródła. Klucz = `heat_sources.type` (kontrakt CEEB). */
export const HEAT_SOURCE_TYPE_LABELS: Record<string, string> = {
  electric_heating: 'Elektryczne',
  photovoltaic: 'Fotowoltaika',
  gas_boiler: 'Gazowy / inne',
  solid_fuel_boiler: 'Kocioł na paliwo stałe',
  solar_collectors: 'Kolektory słoneczne',
  fireplace: 'Kominek',
  tile_stove: 'Piec kaflowy',
  kitchen_stove: 'Piecokuchnia, koza',
  heat_pump: 'Pompa ciepła',
  network: 'Sieć ciepłownicza',
  kitchen_core: 'Trzon kuchenny',
  oil_boiler: 'Kocioł olejowy',
};

/**
 * Kolejność na liście: alfabetycznie wg SKRÓTU, z jednym wyjątkiem — „Kocioł olejowy" na sam
 * koniec jako typ rzadki (feedback właściciela 2026-07-23). Kolejność CEEB nie obowiązuje w UI.
 */
export const HEAT_SOURCE_TYPE_ORDER: string[] = [
  'electric_heating',
  'photovoltaic',
  'gas_boiler',
  'solid_fuel_boiler',
  'solar_collectors',
  'fireplace',
  'tile_stove',
  'kitchen_stove',
  'heat_pump',
  'network',
  'kitchen_core',
  'oil_boiler',
];

/**
 * Typy, które w CEEB NIE MAJĄ pola „Charakter produkowanego ciepła" (capture-forms 2026-08-17,
 * nagłówki mapperów piecokuchnia.js / trzonKuchenny.js). Właściciel 2026-09-13: nie pokazujemy
 * tego wyboru wcale — ani w karcie źródła (schemat), ani w pickerach budynku (mobile/web).
 * Wartość techniczna `heatCharacter` zostaje w danych ('heating' — kolumna NOT NULL), bot jej
 * dla tych typów nie wysyła. LUSTRO: mobile/src/constants/heatSourceLabels.ts (test heatSourceLabelsParity).
 */
export const HEAT_TYPES_WITHOUT_CHARACTER: string[] = [
  'kitchen_stove',
  'kitchen_core',
  'tile_stove', // właściciel 2026-09-14: piec kaflowy też nie ma tego pola w CEEB (mapper piecKaflowy.js)
  'photovoltaic', // Bronek 2026-10-02: „Fotowoltaika bez podawania charakteru ciepła" (mapper fotowoltaika.js: bez charakteru)
];

/** Skróty charakteru ciepła. Klucz = enum wysyłany przez mobilkę. */
export const HEAT_CHARACTER_LABELS: Record<string, string> = {
  heating: 'C.O.',
  hot_water: 'C.W.U.',
  heating_and_hot_water: 'C.O. + C.W.U.',
  mop: 'M.O.P.',
  funkcja_technologiczna: 'Technologiczna',
};

/**
 * Warianty tej samej wartości przychodzące z importu CEEB (`ceebApiImport.js` zapisuje surowy
 * tekst opcji CEEB). Klucze znormalizowane przez `normalizeKey` — dlatego podwójna spacja w
 * „Ogrzewanie (c.o.) +  Ciepła Woda Użytkowa (c.w.u.)" i wielkość liter nie mają znaczenia.
 * Liczby wierszy z pomiaru 2026-07-29 (dev DB) przy każdym wariancie.
 */
const HEAT_CHARACTER_ALIASES: Record<string, string> = {
  'ogrzewanie (c.o.)': 'heating',                                        // 908
  'ciepła woda użytkowa (c.w.u.)': 'hot_water',                          // 3539
  'ogrzewanie (c.o.) + ciepła woda użytkowa (c.w.u.)': 'heating_and_hot_water', // 3087
  'miejscowy ogrzewacz powietrza (m.o.p)': 'mop',                        // 348
  'miejscowy ogrzewacz pomieszczeń': 'mop',                              // 277
  'funkcja technologiczna/produkcyjna': 'funkcja_technologiczna',        // 21
  co_cwu: 'heating_and_hot_water',                                       // 2 — legacy enum mobilki
};

/** Małe litery + zbite białe znaki — CEEB bywa niekonsekwentny w spacjach. */
function normalizeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Surowa wartość z bazy (enum mobilki ALBO tekst z importu CEEB) → skrót do wyświetlenia.
 * Wartość nierozpoznana wraca BEZ ZMIAN — lepiej pokazać surowy tekst niż pusty wiersz.
 * `inne` (843 wiersze) świadomie nie ma skrótu: nie wiadomo, czym jest, więc nie zgadujemy.
 */
export function resolveHeatCharacter(raw: string | null | undefined): string {
  if (!raw) return '';
  if (HEAT_CHARACTER_LABELS[raw]) return HEAT_CHARACTER_LABELS[raw];
  const alias = HEAT_CHARACTER_ALIASES[normalizeKey(raw)];
  if (alias && HEAT_CHARACTER_LABELS[alias]) return HEAT_CHARACTER_LABELS[alias];
  return raw;
}

/** Surowy `type` z bazy → skrót. Nierozpoznany wraca bez zmian (patrz wyżej). */
export function resolveHeatSourceType(raw: string | null | undefined): string {
  if (!raw) return '';
  return HEAT_SOURCE_TYPE_LABELS[raw] || raw;
}
